import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useT, locale } from "../lib/i18n";
import { Plus, Bot, Loader2, Mail, MessageCircle, PenLine, Trash2, Pencil, Users, ChevronDown, ChevronUp, X, Check } from "lucide-react";

const STATUS_NL = { open: "Openstaand", in_progress: "In behandeling", filled: "Ingevuld", closed: "Gesloten" };
const STATUS_TONE = { open: "bg-emerald-50 text-emerald-800 border-emerald-200", in_progress: "bg-amber-50 text-amber-800 border-amber-200", filled: "bg-sky-50 text-sky-800 border-sky-200", closed: "bg-slate-100 text-slate-600 border-slate-200" };
const TRANSFER_NL = { transfer: "Transfer", loan: "Huur", free: "Transfervrij", any: "Alles bespreekbaar" };
const FOOT_NL = { left: "Links", right: "Rechts", both: "Beide" };
const SOURCE = { email: { l: "E-mail", Icon: Mail }, whatsapp: { l: "WhatsApp", Icon: MessageCircle }, sms: { l: "SMS", Icon: MessageCircle }, manual: { l: "Handmatig", Icon: PenLine } };
const EMPTY = { club: "", positions: [], contact_name: "", contact_email: "", contact_phone: "", age_min: "", age_max: "", foot: "", transfer_type: "", budget: "", deadline: "", league: "", notes: "", status: "open" };

export default function RequestsPage() {
  const t = useT();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("open");
  const [position, setPosition] = useState("");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [flash, setFlash] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | request

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (position) params.position = position;
    try { const { data } = await api.get("/requests", { params }); setData(data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, position]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data: r } = await api.post("/requests/scan");
      setFlash(t("{a} berichten gescand · {b} nieuwe aanvraag/aanvragen", { a: r.scanned, b: r.created }));
      await load();
    } finally { setScanning(false); setTimeout(() => setFlash(""), 6000); }
  };
  const positions = data?.positions || {};
  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <Layout title={t("Aanvragen")} subtitle={t("Posities die clubs zoeken · uit e-mail & WhatsApp · handmatig aan te vullen")} compact
      right={
        <div className="flex items-center gap-2">
          <button onClick={scan} disabled={scanning} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="requests-scan-btn">
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />} {t("Scan inbox nu")}
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="requests-new-btn">
            <Plus size={14} /> {t("Nieuwe aanvraag")}
          </button>
        </div>
      }>
      {flash && <div className="mb-3 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="requests-flash">{flash}</div>}

      <PositionStrip positions={positions} counts={data?.open_by_position || {}} active={position} onPick={(p) => setPosition(p === position ? "" : p)} />

      <div className="flex items-center gap-1 border-b border-slate-200 mb-4 overflow-x-auto" data-testid="requests-status-tabs">
        {["open", "in_progress", "filled", "closed", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`requests-tab-${s}`}
            className={`px-3 h-10 text-sm font-bold whitespace-nowrap border-b-2 -mb-px ${status === s ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {s === "all" ? t("Alle") : t(STATUS_NL[s])}{s !== "all" && counts[s] != null && <span className="ml-1 text-[10px] font-mono-tab text-slate-400">{counts[s]}</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-slate-500">{t("Laden…")}</div> : items.length === 0 ? (
        <div className="pm-card p-10 text-center text-sm text-slate-500" data-testid="requests-empty">
          <Users size={28} className="mx-auto mb-2 text-slate-300" />
          {t("Nog geen aanvragen. Scan je inbox of voeg handmatig een aanvraag toe.")}
        </div>
      ) : (
        <div className="space-y-3" data-testid="requests-list">
          {items.map((r) => <RequestCard key={r.id} r={r} positions={positions} onEdit={() => setEditing(r)} onChanged={load} />)}
        </div>
      )}

      {editing && <RequestModal initial={editing} positions={positions} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Layout>
  );
}

function PositionStrip({ positions, counts, active, onPick }) {
  const t = useT();
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="requests-position-strip">
      {Object.entries(positions).map(([code, label]) => {
        const n = counts[code] || 0;
        return (
          <button key={code} onClick={() => onPick(code)} data-testid={`requests-pos-${code}`}
            className={`h-9 px-3 rounded-full border text-xs font-bold flex items-center gap-2 transition-colors ${active === code ? "bg-slate-900 text-white border-slate-900" : n ? "bg-white border-emerald-300 text-slate-800 hover:border-emerald-500" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
            <span className="font-mono-tab">{code}</span><span className="hidden sm:inline font-normal">{t(label)}</span>
            <span className={`px-1.5 rounded-md text-[10px] font-mono-tab ${active === code ? "bg-white/20" : n ? "bg-emerald-50 text-emerald-800" : "bg-slate-100"}`}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

function RequestCard({ r, positions, onEdit, onChanged }) {
  const t = useT();
  const [showMatches, setShowMatches] = useState(false);
  const [matches, setMatches] = useState(null);
  const src = SOURCE[r.source] || SOURCE.manual;
  const setStatus = async (s) => { await api.put(`/requests/${r.id}`, { status: s }); onChanged(); };
  const remove = async () => { if (!window.confirm(t("Aanvraag verwijderen?"))) return; await api.delete(`/requests/${r.id}`); onChanged(); };
  const toggleMatches = async () => {
    if (!showMatches && matches == null) { const { data } = await api.get(`/requests/${r.id}/matches`); setMatches(data.matches || []); }
    setShowMatches((v) => !v);
  };
  const facts = [
    (r.age_min || r.age_max) && `${t("Leeftijd")} ${r.age_min || "…"}–${r.age_max || "…"}`,
    r.foot && `${t("Voet")}: ${t(FOOT_NL[r.foot] || r.foot)}`,
    r.transfer_type && t(TRANSFER_NL[r.transfer_type] || r.transfer_type),
    r.budget && `${t("Budget")}: ${r.budget}`,
    r.deadline && `${t("Deadline")}: ${r.deadline}`,
    r.league && r.league,
  ].filter(Boolean);
  return (
    <div className="pm-card p-4" data-testid={`request-card-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-base tracking-tight" data-testid="request-club">{r.club}</span>
            {r.positions.map((p) => <span key={p} className="pm-pill bg-slate-900 text-white font-mono-tab" title={t(positions[p] || p)}>{p}</span>)}
            <span className={`pm-pill border ${STATUS_TONE[r.status]}`} data-testid="request-status">{t(STATUS_NL[r.status])}</span>
            <span className="pm-pill pm-pill-slate flex items-center gap-1"><src.Icon size={10} /> {t(src.l)}{r.source !== "manual" && r.confidence != null && <span className="text-slate-400">· {Math.round(r.confidence * 100)}%</span>}</span>
          </div>
          {facts.length > 0 && <div className="mt-1 text-xs text-slate-600">{facts.join(" · ")}</div>}
          {(r.contact_name || r.contact_email || r.contact_phone) && (
            <div className="mt-1 text-xs text-slate-500">{[r.contact_name, r.contact_email, r.contact_phone].filter(Boolean).join(" · ")}</div>
          )}
          {r.notes && <div className="mt-2 text-sm text-slate-800">{r.notes}</div>}
          {r.snippet && r.source !== "manual" && <div className="mt-1 text-xs text-slate-400 italic truncate">“{r.subject ? `${r.subject} — ` : ""}{r.snippet}”</div>}
          <div className="mt-1 text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString(locale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            {r.thread_id && <> · <Link to={`/inbox?thread=${encodeURIComponent(r.thread_id)}`} className="text-emerald-700 font-semibold underline underline-offset-2" data-testid="request-open-thread">{t("Open bericht")}</Link></>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <select value={r.status} onChange={(e) => setStatus(e.target.value)} className="pm-input h-8 text-xs" data-testid="request-status-select">
            {Object.keys(STATUS_NL).map((s) => <option key={s} value={s}>{t(STATUS_NL[s])}</option>)}
          </select>
          <button onClick={toggleMatches} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="request-matches-btn"><Users size={12} /> {t("Mijn spelers")} {showMatches ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</button>
          <button onClick={onEdit} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" title={t("Bewerken")} data-testid="request-edit-btn"><Pencil size={13} /></button>
          <button onClick={remove} className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600" title={t("Verwijderen")} data-testid="request-delete-btn"><Trash2 size={13} /></button>
        </div>
      </div>
      {showMatches && (
        <div className="mt-3 border-t border-slate-100 pt-3" data-testid="request-matches">
          {matches == null ? <div className="text-xs text-slate-500">{t("Laden…")}</div> : matches.length === 0 ? <div className="text-xs text-slate-500">{t("Geen spelers in jouw selectie op deze positie.")}</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {matches.map((c) => (
                <Link key={c.id} to={`/clients/${c.id}`} className="rounded-xl border border-slate-200 px-3 py-2 hover:border-emerald-400 flex items-center gap-2" data-testid={`request-match-${c.id}`}>
                  <span className="w-9 h-9 rounded-full bg-slate-900 text-white text-[10px] font-mono-tab flex items-center justify-center shrink-0">{c.position_code}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold truncate">{c.full_name}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{[c.current_club, c.age != null && `${c.age} ${t("jr")}`, c.preferred_foot].filter(Boolean).join(" · ")}</span>
                  </span>
                  <span className={`text-[10px] font-mono-tab px-1.5 py-0.5 rounded-md ${c.match_score >= 0.7 ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{Math.round(c.match_score * 100)}%</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, className = "" }) => <label className={`block text-xs font-bold text-slate-600 ${className}`}>{label}<div className="mt-1 font-normal">{children}</div></label>;

function RequestModal({ initial, positions, onClose, onSaved }) {
  const t = useT();
  const isNew = !initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial, age_min: initial.age_min ?? "", age_max: initial.age_max ?? "", foot: initial.foot || "", transfer_type: initial.transfer_type || "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const togglePos = (p) => set("positions", f.positions.includes(p) ? f.positions.filter((x) => x !== p) : [...f.positions, p]);
  const payload = useMemo(() => ({
    club: f.club, positions: f.positions, contact_name: f.contact_name || null, contact_email: f.contact_email || null, contact_phone: f.contact_phone || null,
    age_min: f.age_min === "" ? null : Number(f.age_min), age_max: f.age_max === "" ? null : Number(f.age_max), foot: f.foot || null, transfer_type: f.transfer_type || null,
    budget: f.budget || null, deadline: f.deadline || null, league: f.league || null, notes: f.notes || null, status: f.status || "open",
  }), [f]);
  const save = async () => {
    if (!f.club.trim()) return setErr(t("Clubnaam is verplicht."));
    if (!f.positions.length) return setErr(t("Kies minimaal één positie."));
    setBusy(true); setErr("");
    try { if (isNew) await api.post("/requests", payload); else await api.put(`/requests/${initial.id}`, payload); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || t("Opslaan mislukt")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="request-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1 font-display font-black text-lg">{isNew ? t("Nieuwe aanvraag") : t("Aanvraag bewerken")}</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="request-modal-close"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("Club")} className="sm:col-span-2"><input className="pm-input w-full" value={f.club} onChange={(e) => set("club", e.target.value)} placeholder="bijv. FC Twente" data-testid="request-club-input" /></Field>
          <div className="sm:col-span-2">
            <div className="text-xs font-bold text-slate-600 mb-1">{t("Gezochte positie(s)")}</div>
            <div className="flex flex-wrap gap-1.5" data-testid="request-positions">
              {Object.entries(positions).map(([code, label]) => (
                <button type="button" key={code} onClick={() => togglePos(code)} data-testid={`request-pos-${code}`}
                  className={`h-8 px-2.5 rounded-full border text-xs font-bold ${f.positions.includes(code) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                  {f.positions.includes(code) && <Check size={10} className="inline mr-1" />}{code} <span className="font-normal hidden sm:inline">· {t(label)}</span>
                </button>
              ))}
            </div>
          </div>
          <Field label={t("Leeftijd min.")}><input type="number" className="pm-input w-full" value={f.age_min} onChange={(e) => set("age_min", e.target.value)} data-testid="request-age-min" /></Field>
          <Field label={t("Leeftijd max.")}><input type="number" className="pm-input w-full" value={f.age_max} onChange={(e) => set("age_max", e.target.value)} data-testid="request-age-max" /></Field>
          <Field label={t("Voet")}>
            <select className="pm-input w-full" value={f.foot} onChange={(e) => set("foot", e.target.value)} data-testid="request-foot"><option value="">—</option>{Object.entries(FOOT_NL).map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
          <Field label={t("Type")}>
            <select className="pm-input w-full" value={f.transfer_type} onChange={(e) => set("transfer_type", e.target.value)} data-testid="request-transfer-type"><option value="">—</option>{Object.entries(TRANSFER_NL).map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
          <Field label={t("Budget")}><input className="pm-input w-full" value={f.budget || ""} onChange={(e) => set("budget", e.target.value)} placeholder="bijv. € 500k – 1M" data-testid="request-budget" /></Field>
          <Field label={t("Deadline / window")}><input className="pm-input w-full" value={f.deadline || ""} onChange={(e) => set("deadline", e.target.value)} placeholder="bijv. 2027-01-31" data-testid="request-deadline" /></Field>
          <Field label={t("Competitie")}><input className="pm-input w-full" value={f.league || ""} onChange={(e) => set("league", e.target.value)} data-testid="request-league" /></Field>
          <Field label={t("Status")}>
            <select className="pm-input w-full" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="request-status-field">{Object.keys(STATUS_NL).map((s) => <option key={s} value={s}>{t(STATUS_NL[s])}</option>)}</select>
          </Field>
          <Field label={t("Contactpersoon")}><input className="pm-input w-full" value={f.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} data-testid="request-contact-name" /></Field>
          <Field label={t("E-mail / telefoon")}><input className="pm-input w-full" value={f.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="request-contact-email" /></Field>
          <Field label={t("Notities")} className="sm:col-span-2"><textarea className="pm-input w-full min-h-[80px]" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="request-notes" /></Field>
          {err && <div className="sm:col-span-2 text-xs text-red-600" data-testid="request-form-error">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-bold" data-testid="request-form-cancel">{t("Annuleren")}</button>
          <button onClick={save} disabled={busy} className="pm-btn-primary h-10 px-4 rounded-lg text-sm font-bold flex items-center gap-1.5" data-testid="request-form-save">{busy && <Loader2 size={13} className="animate-spin" />} {isNew ? t("Toevoegen") : t("Opslaan")}</button>
        </div>
      </div>
    </div>
  );
}
